import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import './AdminCompetition.css';
import { useNavigate } from 'react-router-dom';
import SplitCompetitionUI from './SplitCompetitionUI';

const API_BASE = process.env.REACT_APP_API_URL;

export default function AdminCompetition() {
    const { id } = useParams();
    const [competition, setCompetition] = useState(null);
    const [fencers, setFencers] = useState([]);
    const [allFencers, setAllFencers] = useState([]);
    const [newFencerId, setNewFencerId] = useState('');
    const navigate = useNavigate();
    const [startTime, setStartTime] = useState('');
    const [filterText, setFilterText] = useState('');
    const [selectedRatingType, setSelectedRatingType] = useState('rating_open_longsword');
    const [filterBy, setFilterBy] = useState('name'); // 'name', 'rating', or 'mat'
    const [mat, setMat] = useState(1);
    const [errorMessage, setErrorMessage] = useState("");
    const [numberOfPools, setNumberOfPools] = useState(1);
    const [splitCompetitionNumber, setSplitCompetitionNumber] = useState(0);
    const [displaySplitCompetitionUI, setDisplaySplitCompetitionUI] = useState(false);

    const exportFencersToCSV = () => {
        if (fencers.length === 0) {
            setErrorMessage('No signed-up fencers to export');
            setTimeout(() => setErrorMessage(''), 3000);
            return;
        }
        
        const numberOfPoolsInt = Number(numberOfPools);
        if (!numberOfPoolsInt || numberOfPoolsInt <= 0) {
            setErrorMessage('Number of pools must be a positive number');
            setTimeout(() => setErrorMessage(''), 3000);
            return;
        }
        
        // Sort fencers by rating (descending)
        const sortedFencers = [...fencers].sort((a, b) => {
            const ratingA = a[selectedRatingType] ?? 0;
            const ratingB = b[selectedRatingType] ?? 0;
            return ratingB - ratingA;
        });
        
        const numSplits = 1;
        const chunkSize = Math.ceil(sortedFencers.length / numSplits);
        
        for (let i = 0; i < numSplits; i++) {
            const chunk = sortedFencers.slice(i * chunkSize, (i + 1) * chunkSize);
        
            const seededChunk = chunk.map((f, index) => ({
                ...f,
                seed: Math.floor(index / numberOfPoolsInt) + 1,
            }));
        
            const csvRows = seededChunk.map(f => [
                f.name,
                f.club,
                f.nationality || '',
                f.seed
            ]);
        
            const csvContent = csvRows
                .map(row => row.map(field => String(field).replace(/"/g, '')).join(', '))
            .join('\n');
        
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const filename = numSplits > 1 ? `${competition.name}_Tier${i + 1}.csv` : `${competition.name}.csv`;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    };
                  
  
    useEffect(() => {
        let authorised = localStorage.getItem('adminstatus');
        if (authorised !== 'admingranted') {
            navigate('/');
        }
        fetch(`${API_BASE}/competitions/${id}`)
        .then(res => res.json())
        .then(data => {
            setCompetition(data);
            setStartTime(data.start_time || '');
        });

        fetch(`${API_BASE}/competitions/${id}/fencers`)
            .then(res => res.json())
            .then(setFencers);

        fetch(`${API_BASE}/fencers`)
            .then(res => res.json())
            .then(data => {
                const sortedFencers = data.sort((a, b) => a.name.localeCompare(b.name));
                setAllFencers(sortedFencers);
            });
    }, [id]);

    const saveStartTime = async () => {
        await fetch(`${API_BASE}/competitions/${id}/start-time`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ start_time: startTime }),
        });
  
        setCompetition(prev => ({ ...prev, start_time: startTime }));
    };  

    const toggleActive = async () => {
        const updated = !competition.active;
            await fetch(`${API_BASE}/competitions/${id}/active`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ active: updated }),
            });
        setCompetition(prev => ({ ...prev, active: updated }));
    };

    const renameCompetition = async (newName) => {
        await fetch(`${API_BASE}/competitions/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: newName }),
        });
        setCompetition(prev => ({ ...prev, name: newName }));
    };

    const addFencer = async () => {
        if (!newFencerId) {
            setErrorMessage("Please select a fencer to add.");
            setTimeout(() => setErrorMessage(""), 3000);
            return;
        }
    
        // Check if fencer is already signed up
        const alreadyAdded = fencers.some(f => f.id === newFencerId || String(f.id) === String(newFencerId));
        if (alreadyAdded) {
            setErrorMessage("This fencer is already signed up.");
            setTimeout(() => setErrorMessage(""), 3000);
            return;
        }
    
        try {
            await fetch(`${API_BASE}/competitions/${id}/signup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fencerId: newFencerId }),
            });
    
            const res = await fetch(`${API_BASE}/competitions/${id}/fencers`);
            setFencers(await res.json());
            setNewFencerId("");
        } catch (err) {
            console.error(err);
            setErrorMessage("Failed to add fencer.");
            setTimeout(() => setErrorMessage(""), 3000);
        }
    };   
    
    const removeFencer = async (fencerId) => {
        if (!window.confirm('Are you sure you want to remove this fencer from the competition?')) {
          return;
        }
      
        try {
            const res = await fetch(`${API_BASE}/competitions/${id}/fencers/${fencerId}`, {
                method: 'DELETE',
            });
      
        if (!res.ok) {
            throw new Error('Failed to remove fencer');
        }
      
        // Refresh fencers list after removal
        const updatedFencers = await fetch(`${API_BASE}/competitions/${id}/fencers`).then(r => r.json());
        setFencers(updatedFencers);
        } catch (error) {
            console.error(error);
            setErrorMessage('Error removing fencer');
            setTimeout(() => setErrorMessage(''), 3000);
        }
    };

    const assignMat = async (fencerId) => {
        const matToAssign = Number(mat) === 0 ? null : Number(mat);
    
        try {
            const res = await fetch(`${API_BASE}/competitions/${id}/fencers/mat`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fencerIds: [fencerId],
                    mat: matToAssign,
                }),
            });
    
            if (res.ok) {
                const updatedFencers = await fetch(`${API_BASE}/competitions/${id}/fencers`).then(r => r.json());
                setFencers(updatedFencers);
            } else {
                setErrorMessage('Failed to assign mat');
                setTimeout(() => setErrorMessage(''), 3000);
            }
        } catch (error) {
            console.error(error);
            setErrorMessage('Error assigning mat');
            setTimeout(() => setErrorMessage(''), 3000);
        }
    };
    
    const assignMatAllFencers = () => {
        fencers.forEach(fencer => {
            assignMat(fencer.id);            
        });
    };

    const handleSplitCompetition = () => {
        setDisplaySplitCompetitionUI(!displaySplitCompetitionUI);
    };

    const deleteCompetition = async () => {
        if (!window.confirm('Are you sure you want to delete this competition? This action cannot be undone.')) {
        return;
        }
        await fetch(`${API_BASE}/competitions/${id}`, {
            method: 'DELETE',
        });
        navigate('/admin'); // Navigate back to the competitions list or admin dashboard after deletion
    };
  
    if (!competition) return <div>Loading...</div>;
        return (
            <div className="admin-competition-page">
                <button className='link-button' onClick={() => navigate(`/admin`)} > ← Back</button>
                <h2>Managing: {competition.name}</h2>
                <div className={errorMessage ? 'error-message' : ''}>
                    <p>{errorMessage}</p>
                </div>
                <button
                    onClick={toggleActive}
                    className={competition.active ? 'danger-button' : 'accept-button'}
                >
                    {competition.active ? 'Set Inactive' : 'Set Active'}
                </button>
            {!competition.active && (
                <button 
                    onClick={deleteCompetition} 
                    className="danger-button" 
                    style={{ marginLeft: '10px' }}
                    >
                        Delete Competition
                </button>
            )}

                <div className="competition-header-row">
                    <div className="input-group">
                        <h3>Name</h3>
                        <input
                            defaultValue={competition.name}
                            onBlur={(e) => renameCompetition(e.target.value)}
                        />
                    </div>
                    <div className="input-group">
                        <h3>Start Time</h3>
                        <div className="time-input-row">
                            <input
                                type="time"
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                            />
                            <button onClick={saveStartTime} className="accept-button">
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            <div className="competition-settings">
                <div className="form-row">
                    <div className="input-group">
                        <label htmlFor="rating-type">Type of competition</label>
                        <select
                            id="rating-type"
                            value={selectedRatingType}
                            onChange={(e) => setSelectedRatingType(e.target.value)}
                        >
                            <option value="rating_open_longsword">Open Longsword</option>
                            <option value="rating_women_longsword">Women Longsword</option>
                            <option value="rating_open_saber">Open Saber</option>
                            <option value="rating_women_saber">Women Saber</option>
                            <option value="rating_open_buckler">Open Buckler</option>
                            <option value="rating_open_rapier">Open Rapier</option>
                        </select>
                    </div>

                    <div className="input-group">
                        <label htmlFor="number-of-pools">Number of pools</label>
                        <span className={`pool-size ${fencers.length % numberOfPools !== 0 ? 'uneven' : ''}`}>
                            Pool size: {
                                fencers.length % numberOfPools === 0 
                                ? fencers.length / numberOfPools 
                                : `${Math.floor(fencers.length / numberOfPools)}-${Math.ceil(fencers.length / numberOfPools)} (Uneven)`
                            }
                        </span>
                        <input
                            id="number-of-pools"
                            className="short-input"
                            type="number"
                            value={numberOfPools}
                            onChange={(e) => setNumberOfPools(e.target.value === "" ? "" : Number(e.target.value))}
                        /> 
                    </div>
                    <div>
                        
                    <div className='input-group'>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <select 
                                onChange={(e) => setNewFencerId(e.target.value)} 
                                value={newFencerId} 
                                style={{ minWidth: '250px' }}
                            >
                                <option value="">Add fencer to competition</option>
                                {allFencers
                                .filter(f => f.name.toLowerCase().includes(filterText.toLowerCase()))
                                .map(f => (
                                    <option key={f.id} value={f.id}> {f.name} ({f.club})</option>
                                ))}
                            </select>
                            <button 
                                onClick={addFencer} 
                                className="accept-button" 
                                style={{ width: '60px' }}
                            > Add
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            </div>

                <h3>Signed-Up Fencers</h3>
                <div>
                    <button onClick={exportFencersToCSV} className="accept-button">
                        Export Fencers CSV
                    </button>
                    <select
                        value={filterBy}
                        onChange={(e) => setFilterBy(e.target.value)}
                        style={{ padding: '5px', marginLeft: '10px' }}
                    >
                        <option value="name">Sort Name</option>
                        <option value="rating">Sort Rating</option>
                        <option value="mat">Sort Mat</option>
                    </select>
                    <span style={{marginLeft: '20px', fontSize: '1.2rem', fontFamily: 'Roboto, sans-serif', color: '#FFF'}}>Mat: </span>
                    <input
                        className='mat-input'
                        type="number"
                        value={mat ?? ''}
                        style={{ width: '50px'}}
                        onChange={e => {
                            const val = e.target.value;
                            const num = val === '' ? null : Number(val);
                            setMat(num === 0 ? null : num);
                        }}
                        min="0"
                    />
                    <button className='accept-button' onClick={assignMatAllFencers}>Assign all</button>
                    <button className='accept-button' onClick={handleSplitCompetition}>Tiered Competition</button>
                </div>
                {displaySplitCompetitionUI && 
                <SplitCompetitionUI 
                    setDisplaySplitCompetitionUI={setDisplaySplitCompetitionUI} 
                    fencers={fencers}
                    splitCompetitionNumber={splitCompetitionNumber}
                    setSplitCompetitionNumber={setSplitCompetitionNumber}
                    selectedRatingType={selectedRatingType}
                />
                }
                <h3 className="fencer-count">Competition size: {fencers.length}</h3>
                <div className="fencers-mat-container">
                <ul className="fencer-wrapper admin-competition-page">
                    {[...fencers]
                        .filter(f => {
                        if (!filterText) return true;
                        const text = filterText.toLowerCase();
                        if (filterBy === 'name') return f.name.toLowerCase().includes(text);
                        if (filterBy === 'rating') return String(f[selectedRatingType] ?? '').includes(text);
                        if (filterBy === 'mat') {
                            if (['none', 'no', '-'].includes(text)) return f.mat === null || f.mat === 0;
                            return String(f.mat ?? '').includes(text);
                        }
                        return true;
                        })
                        .sort((a, b) => {
                        if (filterBy === 'name') return a.name.localeCompare(b.name);
                        if (filterBy === 'rating') return (b[selectedRatingType] ?? 0) - (a[selectedRatingType] ?? 0);
                        if (filterBy === 'mat') return (a.mat ?? 0) - (b.mat ?? 0);
                        return 0;
                        })
                        .map(f => (
                        <li className="fencer-item" key={f.id}>
                            <span className='fence-name'>{f.name}</span>
                            <span className='fence-club'>{f.club}</span>
                            <span className='fence-rating'>rating: {f[selectedRatingType] ?? '—'}</span>
                            <span className='fence-mat-button'>
                            <button onClick={() => assignMat(f.id)} className="accept-button" style={{ marginRight: '10px' }}>
                                Assign mat
                            </button>
                            <span>{f.mat ? `Mat: ${f.mat}` : 'Mat: -'}</span>
                            </span>
                            <button
                            onClick={() => removeFencer(f.id)}
                            className='fence-delete-button danger-button'
                            >
                            Remove
                            </button>
                        </li>
                        ))}
                    </ul>
            </div>    
    </div>
);
}
